import React, { useEffect, useMemo, useState } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import ExternalMessageService from '../services/ExternalMessageService';
import { useStudentPortal } from '../store/StudentPortalContext';
import { formatDateTime } from '../utils/dateFormat';

const sortByDate = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

export default function StudentPortalNotificationsPage() {
  const { student } = useStudentPortal();
  const messageService = useMemo(() => new ExternalMessageService(), []);
  const [activeId, setActiveId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [, setRefreshTick] = useState(0);

  const notifications = student
    ? messageService.listForStudent(student.id).sort(sortByDate)
    : [];

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRefreshTick((prev) => prev + 1);
    }, 8000);
    return () => window.clearInterval(intervalId);
  }, []);

  const unreadCount = notifications.filter((message) => !message.readBy?.includes(student?.id)).length;
  const filtered = notifications.filter((message) => {
    if (!searchQuery) return true;
    const haystack = `${message.subject} ${message.body} ${message.fromName}`.toLowerCase();
    return haystack.includes(searchQuery.toLowerCase());
  });

  const activeNotification = filtered.find((message) => message.id === activeId) ||
    notifications.find((message) => message.id === activeId) ||
    null;

  const markAllRead = () => {
    if (!student) return;
    notifications.forEach((message) => {
      if (!message.readBy?.includes(student.id)) {
        messageService.markRead(message.id, student.id);
      }
    });
    setRefreshTick((prev) => prev + 1);
  };

  const handleSelect = (message) => {
    if (!student || !message) return;
    setActiveId(message.id);
    if (message.fromType === 'staff' && !message.readBy?.includes(student.id)) {
      messageService.markRead(message.id, student.id);
      setRefreshTick((prev) => prev + 1);
    }
  };

  if (!student) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Sign in to view notifications.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sp-portal-hero sp-portal-glow">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Notifications</h2>
            <p className="sp-muted">Updates from your teachers, rewards, and assignments.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={unreadCount > 0 ? 'warning' : 'inactive'}>
              {unreadCount} unread
            </Badge>
            <Button size="sm" variant="secondary" onClick={markAllRead} disabled={unreadCount === 0}>
              Mark all read
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
        <Card className="sp-portal-card space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="sp-label">Notification feed</div>
            <input
              className="sp-input w-full md:w-64"
              placeholder="Search notifications"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>
          {filtered.length === 0 ? (
            <div className="sp-panel sp-panel--muted p-3 text-sm">No notifications found.</div>
          ) : (
            <div className="space-y-2">
              {filtered.map((message) => {
                const isUnread = !message.readBy?.includes(student.id);
                return (
                  <button
                    key={message.id}
                    type="button"
                    className={`sp-panel w-full p-3 text-left ${isUnread ? 'sp-panel--warning' : 'sp-panel--muted'}`}
                    onClick={() => handleSelect(message)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="sp-value" style={{ fontWeight: 600 }}>{message.subject}</div>
                      <div className="text-xs sp-muted">{formatDateTime(message.createdAt)}</div>
                    </div>
                    <div className="text-xs sp-muted">From: {message.fromName}</div>
                    {message.category && (
                      <div className="text-xs sp-muted mt-1">Type: {message.category.replace('-', ' ')}</div>
                    )}
                    {isUnread && <div className="text-xs sp-warning-text mt-1">New</div>}
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="sp-portal-card space-y-3">
          {!activeNotification ? (
            <div className="text-sm sp-muted">Select a notification to view details.</div>
          ) : (
            <>
              <div>
                <div className="sp-value" style={{ fontWeight: 600 }}>{activeNotification.subject}</div>
                <div className="text-xs sp-muted">
                  {activeNotification.fromName} · {formatDateTime(activeNotification.createdAt)}
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap">
                {activeNotification.body || 'No message body.'}
              </div>
              {activeNotification.attachments?.length > 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-xs space-y-1">
                  <div className="sp-label">Attachments</div>
                  {activeNotification.attachments.map((file, idx) => (
                    <div key={`${file.name}-${idx}`}>{file.name}</div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
